import React, { useState, useEffect } from "react";
import AdminLayout from "../../Layout/index.jsx";
import {
  useGetCampusQuery,
  useGetClustersByCampusQuery,
  useGetStaffByEmailIdQuery,
  useGetStudentsByCampusQuery,
} from "../../../../graphql/generated.tsx";
import { Table, TextInput, Button, Badge } from "flowbite-react";
import ReactSelect from "react-select";
import { MdOutlinePersonOff } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import LightSpinner from "../../../../components/LightSpinner.jsx";
import Pagination from "../../../../components/Pagination/index.jsx";
import Tour from "reactour";
import { LuMousePointerClick } from "react-icons/lu";


const StudentLookup = () => {
  const navigate = useNavigate();
  const email = JSON.parse(localStorage.getItem("user"))?.email;
  const helperMessage = "Click any student row to open the individual dashboard.";
  
  // Fetch staff data
  const { data: staffData, loading: staffLoading } = useGetStaffByEmailIdQuery({
    variables: { email },
  });

  // Fetch campus data
  const { data: campusDetails, loading: campusLoading } = useGetCampusQuery({
    variables: { id: staffData?.getStaffByEmailId?.campusId },
    skip: !staffData,
  });
  const campus = campusDetails?.campus;

  // Fetch clusters data
  const { data: clustersData, loading: clustersLoading } =
    useGetClustersByCampusQuery({
      variables: { campusName: campus?.name, page: 1, limit: 40 },
      skip: !campus,
    });

  const availableClusters = staffData?.getStaffByEmailId?.isAdminStaff
    ? clustersData?.getClustersByCampus
    : clustersData?.getClustersByCampus?.filter((cluster) =>
        staffData?.getStaffByEmailId?.clusterIds?.includes(cluster.id)
      );

  // Sort clusters alphabetically
  const sortedClusters = availableClusters ? [...availableClusters].sort((a, b) => a.name.localeCompare(b.name)) : [];
  const clusterOptions = sortedClusters.map((cluster) => ({
    value: cluster.id,
    label: cluster.name,
  }));

  // Student lookup state
  const [selectedCluster, setSelectedCluster] = useState(() => sortedClusters?.[0]?.id || "");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [allStudents, setAllStudents] = useState([]);
  const [isMinLoadTime, setIsMinLoadTime] = useState(true);
  const [typedHelperText, setTypedHelperText] = useState("");
  const [shouldStartTyping, setShouldStartTyping] = useState(false);
  const studentsPerPage = 20;

  // Minimum load time for spinner (2 seconds)
  useEffect(() => {
    setTimeout(() => {
      setIsMinLoadTime(false);
      // Start typing after loader completes
      setShouldStartTyping(true);
    }, 2000);
  }, []);

  // Play typing effect only after loader completes
  useEffect(() => {
    if (!shouldStartTyping) return;

    setTypedHelperText("");
    let currentIndex = 0;

    const typingTimer = setInterval(() => {
      currentIndex += 1;
      setTypedHelperText(helperMessage.slice(0, currentIndex));

      if (currentIndex >= helperMessage.length) {
        clearInterval(typingTimer);
      }
    }, 40);

    return () => clearInterval(typingTimer);
  }, [shouldStartTyping]);

  // Debounce search term (500ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Set initial cluster when available
  useEffect(() => {
    if (sortedClusters && sortedClusters.length > 0 && !selectedCluster) {
      setSelectedCluster(sortedClusters[0]?.id);
    }
  }, [sortedClusters, selectedCluster]);

  // Fetch students data
  const {
    data: studentData,
    loading: studentLoading,
    refetch,
  } = useGetStudentsByCampusQuery({
    variables: {
      campusId: campus?.id,
      clusterid: selectedCluster,
      searchTerm: debouncedSearchTerm,
      page: page,
      limit: studentsPerPage,
    },
    skip: !campus?.id || !selectedCluster,
    fetchPolicy: "cache-and-network",
  });

  // Store all students when data changes
  useEffect(() => {
    if (studentData?.getStudentsByCampus?.students) {
      setAllStudents(studentData.getStudentsByCampus.students);
    }
  }, [studentData]);

  // Refetch when cluster changes
  useEffect(() => {
    if (selectedCluster && campus?.id) {
      setPage(1);
      refetch({
        campusId: campus.id,
        clusterid: selectedCluster,
        searchTerm: debouncedSearchTerm,
        page: 1,
        limit: studentsPerPage,
      });
    }
  }, [selectedCluster, campus?.id, refetch]);

  useEffect(() => {
    if (campus?.id && selectedCluster) {
      refetch({
        campusId: campus.id,
        clusterid: selectedCluster,
        searchTerm: debouncedSearchTerm,
        page: page,
        limit: studentsPerPage,
      });
    }
  }, [page, debouncedSearchTerm, campus?.id, selectedCluster, refetch]);

  const paginatedStudents = studentData?.getStudentsByCampus?.students || [];
  const totalStudents = studentData?.getStudentsByCampus?.total || 0;
  const totalPages = Math.ceil(totalStudents / studentsPerPage);

  const highlightMatch = (text) => {
    if (!searchTerm || !text) return text;
    const regex = new RegExp(`(${searchTerm})`, "gi");
    return text.split(regex).map((part, i) =>
      regex.test(part) ? <mark key={i} className="bg-yellow-200">{part}</mark> : part
    );
  };

  // Reset to page 1 when debounced search term changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  // Tour state
  const [isTourOpen, setIsTourOpen] = useState(false);

  const clusterSelectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: 40,
      borderRadius: 6,
      borderColor: "#d1d5db",
      backgroundColor: "#ffffff",
      boxShadow: state.isFocused ? "0 0 0 1px #9ca3af" : "none",
      "&:hover": {
        borderColor: "#9ca3af",
      },
    }),
    valueContainer: (base) => ({
      ...base,
      padding: "2px 10px",
    }),
    singleValue: (base) => ({
      ...base,
      color: "#111827",
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#d1d5db"
        : state.isFocused
        ? "#f3f4f6"
        : "#ffffff",
      color: "#111827",
      ":active": {
        backgroundColor: "#d1d5db",
      },
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    menu: (base) => ({
      ...base,
      zIndex: 30,
    }),
  };

  useEffect(() => {
    const permissions = JSON.parse(localStorage.getItem("tourPermissions"));
    
    if (permissions?.showStudentLookupTour && !studentLoading && !staffLoading) {
      setTimeout(() => setIsTourOpen(true), 800);
    }
  }, [studentLoading, staffLoading]);

  const closeTour = () => {
    setIsTourOpen(false);
    const permissions = JSON.parse(localStorage.getItem("tourPermissions"));
    if (permissions) {
      permissions.showStudentLookupTour = false;
      localStorage.setItem("tourPermissions", JSON.stringify(permissions));
    }
    localStorage.removeItem("triggerStudentLookupTour");
  };

  const openTour = () => {
    setIsTourOpen(true);
  };

  const tourSteps = [
    {
      selector: '[data-tour="cluster-select"]',
      content: "Select a batch/cluster to view students enrolled in it.",
    },
    {
      selector: '[data-tour="search-input"]',
      content: "Search by name, roll no, or email to quickly find a student.",
    },
    {
      selector: '[data-tour="student-row"]',
      content: "Click on a student row to view their individual progress and details.",
    },
  ];

  // Show loading spinner only on initial data load
  if (staffLoading || clustersLoading || campusLoading || isMinLoadTime) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          <LightSpinner message="Assignment and course data loading" />
        </div>
      </AdminLayout>
    );
  }

  // Show error if no clusters available
  if (!sortedClusters || sortedClusters.length === 0) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-600">No batches available</h2>
            <p className="text-gray-500">Please contact your administrator.</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-full sm:p-0 md:p-0 font-poppins">
        <Tour
          steps={tourSteps}
          isOpen={isTourOpen}
          onRequestClose={(target) => {
            const permissions = JSON.parse(localStorage.getItem("tourPermissions"));
            if (permissions) {
              if (target?.type === 'click' && target?.target?.closest('[data-tour-elem="close"]')) {
                permissions.showStudentLookupTour = false;
                permissions.showStaffReportTour = false;
                permissions.showAllStudentsTour = false;
                permissions.showStaffDashboardTabsTour = false;
              } else {
                permissions.showStudentLookupTour = false;
                permissions.showStaffReportTour = false;
                permissions.showAllStudentsTour = false;
                permissions.showStaffDashboardTabsTour = false;
              }
              localStorage.setItem("tourPermissions", JSON.stringify(permissions));
            }
            setIsTourOpen(false);
          }}
          accentColor="#203c59"
          rounded={12}
          showNumber={false}
          showNavigation={true}
          showButtons={true}
          showCloseButton={true}
          lastStepNextButton={
            <button
              style={{
                backgroundColor: "#203c59",
                color: "white",
                padding: "10px 20px",
                borderRadius: "8px",
                border: "none",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "500",
              }}
            >
              Got it!
            </button>
          }
          nextButton={
            <button
              style={{
                backgroundColor: "#203c59",
                color: "white",
                padding: "8px 20px",
                borderRadius: "8px",
                border: "none",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "500",
              }}
            >
              Next →
            </button>
          }
          prevButton={
            <button
              style={{
                backgroundColor: "transparent",
                color: "#6b7280",
                padding: "8px 20px",
                borderRadius: "8px",
                border: "1px solid #d1d5db",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "500",
              }}
            >
              ← Back
            </button>
          }
          closeWithMask={false}
          disableInteraction={true}
        />

        <div className="max-w-full px-6 pt-3 pb-3 bg-white border border-b-0 border-gray-200 shadow-md sm:px-6 rounded-t-xl">
          <div className="relative w-full sm:w-[760px] max-w-full overflow-hidden rounded-xl border border-sky-300 bg-sky-100 px-5 py-4 shadow-sm min-h-[96px]">
            <div className="absolute inset-y-0 left-0 w-1 bg-sky-900" />
            <div className="flex items-center gap-2.5 pl-2">
              <div className="flex items-center justify-center flex-shrink-0 w-9 h-9 bg-white border border-sky-200 rounded-full shadow-sm">
                <LuMousePointerClick className="text-sky-900" size={18} />
              </div>
              <div className="min-w-0">
                <h3 className="text-base font-semibold leading-tight text-slate-800 sm:text-lg">
                  View Dashboard
                </h3>
                <p className="mt-1 text-sm font-medium text-slate-700 sm:text-base">
                  {typedHelperText}
                  {typedHelperText.length < helperMessage.length && (
                    <span className="inline-block w-0.5 h-4 ml-0.5 bg-slate-700 animate-pulse"></span>
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Top Filter Layer */}
        <div className="max-w-full p-4 mb-0 bg-white border border-t-0 border-b-0 border-gray-200 shadow-md sm:p-6">
          {/* Controls */}
          <div className="flex flex-col items-stretch justify-between gap-3 sm:flex-row sm:items-end sm:gap-4">
            <div className="flex flex-col items-stretch w-full gap-3 sm:flex-row sm:items-center sm:gap-4 sm:w-auto">
              <div data-tour="cluster-select" className="w-full sm:w-auto">
                <p className="mb-1 text-sm font-semibold text-gray-600">Batch details</p>
                <ReactSelect
                  isSearchable={false}
                  isDisabled={sortedClusters?.length === 1}
                  options={clusterOptions}
                  value={clusterOptions.find((option) => option.value === selectedCluster) || null}
                  onChange={(option) => setSelectedCluster(option?.value || "")}
                  styles={clusterSelectStyles}
                />
              </div>
            </div>
            <div className="flex flex-col items-stretch w-full gap-3 sm:flex-row sm:items-center sm:gap-4 sm:w-auto">
              <div data-tour="search-input" className="relative w-full sm:w-auto">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by name, roll no, or email"
                  className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg sm:w-80 focus:ring-2 focus:ring-blue-500 focus:border-transparent sm:text-base font-poppins"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm("")}
                    className="absolute text-gray-400 transform -translate-y-1/2 right-3 top-1/2 hover:text-gray-600"
                  >
                    ✕
                  </button>
                )}
              </div>
              <Badge color="info" className="flex items-center justify-center h-[40px] px-4 text-sm sm:text-base font-poppins">{totalStudents} Students</Badge>
            </div>
          </div>
        </div>

        {/* Skeleton loader during pagination/search refetch */}
        {studentLoading && (
          <div className="max-w-full min-h-screen p-4 pb-10 bg-white border border-t-0 border-gray-200 shadow-md sm:p-6 sm:pb-12 rounded-b-xl">
            <div className="-mx-4 overflow-x-auto sm:mx-0">
              <Table className="min-w-full text-center border divide-y divide-gray-200 rounded-lg dark:divide-gray-700 font-poppins">
                <Table.Head style={{backgroundColor: '#5c7a94'}} className="text-white">
                  <Table.HeadCell style={{backgroundColor: '#5c7a94'}} className="text-white">S.No</Table.HeadCell>
                  <Table.HeadCell style={{backgroundColor: '#5c7a94'}} className="text-white">Name</Table.HeadCell>
                  <Table.HeadCell style={{backgroundColor: '#5c7a94'}} className="text-white">Roll No</Table.HeadCell>
                  <Table.HeadCell style={{backgroundColor: '#5c7a94'}} className="text-white">Department</Table.HeadCell>
                  <Table.HeadCell style={{backgroundColor: '#5c7a94'}} className="text-white">Email</Table.HeadCell>
                </Table.Head>
                <Table.Body className="bg-white dark:bg-gray-900">
                  {Array.from({ length: studentsPerPage }).map((_, idx) => (
                    <Table.Row key={idx}>
                      <Table.Cell className="px-2 py-2 sm:px-4 sm:py-3 whitespace-nowrap">
                        <div className="w-6 h-4 mx-auto bg-gray-200 rounded animate-pulse"></div>
                      </Table.Cell>
                      <Table.Cell className="px-2 py-2 sm:px-4 sm:py-3 whitespace-nowrap">
                        <div className="w-32 h-4 mx-auto bg-gray-200 rounded animate-pulse"></div>
                      </Table.Cell>
                      <Table.Cell className="px-2 py-2 sm:px-4 sm:py-3 whitespace-nowrap">
                        <div className="w-24 h-4 mx-auto bg-gray-200 rounded animate-pulse"></div>
                      </Table.Cell>
                      <Table.Cell className="px-2 py-2 sm:px-4 sm:py-3 whitespace-nowrap">
                        <div className="h-4 mx-auto bg-gray-200 rounded w-28 animate-pulse"></div>
                      </Table.Cell>
                      <Table.Cell className="px-2 py-2 sm:px-4 sm:py-3 whitespace-nowrap">
                        <div className="w-40 h-4 mx-auto bg-gray-200 rounded animate-pulse"></div>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>
          </div>
        )}

        {/* Table - Bottom Layer */}
        {!studentLoading && paginatedStudents.length > 0 && (
          <div className="max-w-full min-h-screen p-4 pb-10 bg-white border border-t-0 border-gray-200 shadow-md sm:p-6 sm:pb-12 rounded-b-xl">
            <div className="-mx-4 overflow-x-auto sm:mx-0">
              <Table className="min-w-full text-center border divide-y divide-gray-200 rounded-lg dark:divide-gray-700 font-poppins">
              <Table.Head style={{backgroundColor: '#5c7a94'}} className="text-white">
                <Table.HeadCell style={{backgroundColor: '#5c7a94'}} className="text-white">S.No</Table.HeadCell>
                <Table.HeadCell style={{backgroundColor: '#5c7a94'}} className="text-white">Name</Table.HeadCell>
                <Table.HeadCell style={{backgroundColor: '#5c7a94'}} className="text-white">Roll No</Table.HeadCell>
                <Table.HeadCell style={{backgroundColor: '#5c7a94'}} className="text-white">Department</Table.HeadCell>
                <Table.HeadCell style={{backgroundColor: '#5c7a94'}} className="text-white">Email</Table.HeadCell>
              </Table.Head>
              <Table.Body className="bg-white dark:bg-gray-900">
                {paginatedStudents.map((student, idx) => (
                  <Table.Row
                    key={student.id}
                    className="transition duration-150 ease-in-out cursor-pointer hover:bg-blue-50 dark:hover:bg-gray-800"
                    onClick={() => navigate(`/admin/student/${student.id}`)}
                    data-tour={idx === 0 ? "student-row" : ""}
                  >
                    <Table.Cell className="px-2 py-2 text-xl text-gray-600 sm:px-4 sm:py-3 sm:text-base dark:text-gray-400 whitespace-nowrap">
                      {(page - 1) * studentsPerPage + idx + 1}
                    </Table.Cell>
                    <Table.Cell className="px-2 py-2 text-xl text-gray-800 sm:px-4 sm:py-3 sm:text-base dark:text-gray-200 whitespace-nowrap">
                      {highlightMatch(student.name)}
                    </Table.Cell>
                    <Table.Cell className="px-2 py-2 text-xl text-gray-600 sm:px-4 sm:py-3 sm:text-base dark:text-gray-400 whitespace-nowrap">
                      {highlightMatch(student.rollNo)}
                    </Table.Cell>
                    <Table.Cell className="px-2 py-2 text-xl text-gray-600 sm:px-4 sm:py-3 sm:text-base dark:text-gray-400 whitespace-nowrap">
                      {student.department && String(student.department).trim() !== "" ? highlightMatch(student.department) : "-"}
                    </Table.Cell>
                    <Table.Cell className="px-2 py-2 text-xl text-gray-600 sm:px-4 sm:py-3 sm:text-base dark:text-gray-400 whitespace-nowrap">
                      {highlightMatch(student.email)}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
            </div>

            {/* Pagination */}
            <Pagination 
              currentPage={page} 
              totalPages={totalPages} 
              onPageChange={setPage} 
            />
          </div>
        )}

        {/* Empty state */}
        {!studentLoading && paginatedStudents.length === 0 && (
          <div className="max-w-full min-h-screen p-4 pb-10 bg-white border border-t-0 border-gray-200 shadow-md sm:p-6 sm:pb-12 rounded-b-xl">
            <div className="flex flex-col items-center justify-center h-[400px] sm:h-[500px] text-center px-4">
              <h2 className="mb-2 text-lg font-bold text-gray-600 sm:text-xl md:text-2xl">
                {searchTerm ? 'No matching students found' : 'Oops, No students are enrolled in this batch'}
              </h2>
              <p className="text-sm sm:text-base text-gray-500 mb-6 max-w-full sm:max-w-md lg:max-w-[500px]">
                {searchTerm 
                  ? `No results for "${searchTerm}". Try a different search term.`
                  : 'We couldn\'t find any students. Try selecting a different batch.'}
              </p>
            </div>
          </div>
        )}
        
        <button
          onClick={openTour}
          style={{ backgroundColor: "#053859" }}
          className="absolute z-10 flex items-center justify-center w-10 h-10 text-white rounded-full shadow-lg sm:w-8 sm:h-8 top-3 right-3 sm:top-4 sm:right-4 hover:bg-opacity-90 focus:ring-4 focus:ring-blue-300"
          aria-label="Help"
        >
          <span className="text-base font-bold sm:text-lg">?</span>
        </button>
      </div>
    </AdminLayout>
  );
};

export default StudentLookup;